import React, { useEffect, useState, useMemo } from "react";
import ReactTable from "react-table-v6";
import { Link, useLocation } from "react-router-dom";
import { formatDate } from "../../../../../../formatDate";
import commaNumber from "comma-number";
import Loader from "../../../../../../settings/loader";
import "react-table-v6/react-table.css";
import "./style.scss";

function Slider4(props) {
  const location = useLocation();
  const [data, setData] = useState({
    result: null,
    table: [],
  });
  const [load, setLoad] = useState(0);

  // Initialize data when props.data changes
  useEffect(() => {
    if (props.data && !load) {
      setData((prev) => ({
        ...prev,
        result: {
          ...props.filter.data,
          ...props.data,
        },
      }));
      setLoad(1);
    }
    // eslint-disable-next-line
  }, [props.data, props.filter, load]);

  // Prepare table data when result is set
  useEffect(() => {
    if (load === 1 && data.result) {
      const table = data.result.sales.data.sort(sortSales).map((row) => {
        const chain = data.result.chains.find(
          (chain) => chain.chaKey === row.chaKey
        );
        const brand = data.result.brands.find(
          (brand) => brand.braKey === row.braKey
        );
        return {
          ...row,
          businessName: `${brand?.name || ""} - ${chain?.name || ""}`,
          currency: chain?.currency?.toUpperCase() || "",
        };
      });
      setData((prev) => ({
        ...prev,
        table,
      }));
      setLoad(2);
    }
    // eslint-disable-next-line
  }, [load, data.result]);

  // Export logic
  useEffect(() => {
    if (props.export && data.table.length) {
      props.getData({
        type: "table",
        title: "Void Summary",
        filter: {
          brands: props.filter.data.brands.map((brand) => brand.name),
          chains: props.filter.data.chains.map((chain) => chain.name),
          date: {
            start: props.filter.data.date.start,
            end: props.filter.data.date.end,
          },
        },
        body: [
          {
            head: [
              [
                "Status",
                "Bill No.",
                "Total",
                "Employee",
                "Customers",
                "Session No.",
                "Date / Time",
                "Business Name",
              ],
            ],
            body: data.table.map((value) => [
              value.status ? "Closed" : "Open",
              value.billNo,
              `${value?.currency} ${commaNumber(value.total.toFixed(2))}`,
              value.emp?.[0]?.name ||
                value.employee?.name ||
                value.empName ||
                "",
              value.customers,
              value.sesNo,
              formatDate.long(value.date),
              value.businessName,
            ]),
          },
        ],
      });
    }
    // eslint-disable-next-line
  }, [props.export, data.table, props.getData, props.filter]);

  function sortSales(a, b) {
    if (parseInt(a.date) > parseInt(b.date)) return -1;
    if (parseInt(a.date) < parseInt(b.date)) return 1;
    return 0;
  }

  // Render link for table cells
  const renderLink = (component) => (
    <Link
      to={`/${location.pathname.split("/")[1]}/view/sales/${
        component.data._id
      }`}
      state={{ prevUrl: location.pathname }}
    >
      {component.value}
    </Link>
  );

  // Unique business names for filter dropdown
  const uniqueNames = useMemo(() => {
    if (!data.table) return [];
    return [...new Set(data.table.map((table) => table.businessName))];
  }, [data.table]);

  const columns = [
    {
      Header: "Business Name",
      accessor: "businessName",
      filterMethod: (filter, row) => {
        if (filter.value === "all") return true;
        return row[filter.id] === filter.value;
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">All</option>
          {uniqueNames.map((name) => (
            <option value={name} key={name}>
              {name}
            </option>
          ))}
        </select>
      ),
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Status",
      accessor: "status",
      filterMethod: (filter, row) => {
        if (filter.value === "all") return true;
        if (filter.value === "Open") return row[filter.id] === 0;
        if (filter.value === "Closed") return row[filter.id] === 1;
        return false;
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">All</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
        </select>
      ),
      Cell: (row) =>
        renderLink({
          data: row.row._original,
          value: (
            <div className={"name"}>
              <div className={row.value ? "open" : ""} />
              {row.value ? "Closed" : "Open"}
            </div>
          ),
        }),
    },

    {
      Header: "Bill No",
      accessor: "billNo",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Total",
      accessor: "total",
      Cell: (row) =>
        `${row?.original?.currency} ${commaNumber(row.value.toFixed(2))}`,
    },
    {
      Header: "Employee",
      accessor: "employee",
      Cell: (row) => {
        // Extract employee name from emp array or employee field
        const employee =
          row.row._original?.emp?.[0]?.name ||
          row.row._original?.employee?.name ||
          row.row._original?.empName ||
          "";
        return renderLink({ data: row.row._original, value: employee });
      },
    },
    {
      Header: "Customers",
      accessor: "customers",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Session No.",
      accessor: "sesNo",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Date / Time",
      accessor: "date",
      Cell: (row) =>
        renderLink({
          data: row.row._original,
          value: formatDate.long(row.value),
        }),
    },
  ];

  return (
    <div>
      {load < 2 ? (
        <Loader />
      ) : (
        <ReactTable
          className="-striped -highlight"
          data={data.table}
          columns={columns}
          sortable
          filterable
          getTdProps={() => ({
            style: {
              textAlign: "center",
            },
          })}
        />
      )}
    </div>
  );
}

export default Slider4;
